import { ENTITY_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"

// Packets have no drawer entry of their own. A packet bundles records of exactly one catalogue
// (`type`, fixed at creation), so it is listed with that catalogue: Materialien, Maschinen and Zusatzleistungen each
// show their records and their packets in one table, narrowed by the kind filter above it
// (`LewCatalogKindFilter`, `?kind=`). This map is the one place that pairs a packet type with the
// catalogue it belongs to — the routes, the overviews, the drawer, the palette, the Protokoll tab's
// "Alle anzeigen" and the Erklärung all read it. The one list of every packet, `PacketOverview`, is
// reached through the command palette only. See docs/navigation.md, "Packets live with their catalogue".

export const CATALOG_PACKETS = {
  MATERIAL: {
    catalog: "MaterialOverview",
    // The catalogue's own create form, offered by the overview's create button beside the packet one.
    catalogCreate: "MaterialCreate",
    catalogCreateKey: "material.overview.actions.create_new",
    catalogCreateHelp: "material-create",
    catalogEditPermission: PERMISSIONS.EDIT_MATERIAL_GENERAL,
    // The old packet list's route, kept as a redirect onto the catalogue filtered to packets.
    packets: "MaterialPacketOverview",
    packetsPath: "/material/packets",
    create: "PacketCreateMaterial",
    createPath: "/packet/create/material",
    icon: ENTITY_ICONS.material,
    catalogTitleKey: "material.overview.title",
    // The catalogue's drawer name: the parent a palette row names a packet list and a create form by.
    catalogNavKey: "nav.material.overview",
    // `nav.packet.*` keys: the heading and drawer name for somebody who may read the packets alone.
    packetsTitleKey: "nav.packet.material",
    packetsSubtitleKey: "packet.overview.subtitle_material",
    createTitleKey: "packet.create.material.title",
    createSubtitleKey: "packet.create.material.subtitle",
    catalogPermission: PERMISSIONS.VIEW_MATERIAL_GENERAL,
  },
  MACHINE: {
    catalog: "MachineOverview",
    catalogCreate: "MachineCreate",
    catalogCreateKey: "machine.overview.actions.create_new",
    catalogCreateHelp: "machine-create",
    catalogEditPermission: PERMISSIONS.EDIT_MACHINE_GENERAL,
    packets: "MachinePacketOverview",
    packetsPath: "/machine/packets",
    create: "PacketCreateMachine",
    createPath: "/packet/create/machine",
    icon: ENTITY_ICONS.machine,
    catalogTitleKey: "machine.overview.title",
    catalogNavKey: "nav.machine.overview",
    packetsTitleKey: "nav.packet.machine",
    packetsSubtitleKey: "packet.overview.subtitle_machine",
    createTitleKey: "packet.create.machine.title",
    createSubtitleKey: "packet.create.machine.subtitle",
    catalogPermission: PERMISSIONS.VIEW_MACHINE_GENERAL,
  },
  ADDITIONAL_EFFORT: {
    catalog: "AdditionalEffortOverview",
    catalogCreate: "AdditionalEffortCreate",
    catalogCreateKey: "additionaleffort.overview.actions.create_new",
    catalogCreateHelp: "additionaleffort-create",
    catalogEditPermission: PERMISSIONS.EDIT_ADDITIONAL_EFFORT_GENERAL,
    packets: "AdditionalEffortPacketOverview",
    packetsPath: "/additionaleffort/packets",
    create: "PacketCreateAdditionalEffort",
    createPath: "/packet/create/additionaleffort",
    icon: ENTITY_ICONS.additionaleffort,
    catalogTitleKey: "additionaleffort.overview.title",
    catalogNavKey: "nav.additionaleffort.overview",
    packetsTitleKey: "nav.packet.additional_effort",
    packetsSubtitleKey: "packet.overview.subtitle_additional_effort",
    createTitleKey: "packet.create.additional_effort.title",
    createSubtitleKey: "packet.create.additional_effort.subtitle",
    catalogPermission: PERMISSIONS.VIEW_ADDITIONAL_EFFORT_GENERAL,
  },
}

/** The packet types in the order the drawer lists their catalogues. */
export const PACKET_TYPES = ["MATERIAL", "MACHINE", "ADDITIONAL_EFFORT"]

/** The values of the overview's `kind` query: records and packets together, or one of the two. */
export const CATALOG_KINDS = { ALL: "all", ITEMS: "items", PACKETS: "packets" }

/**
 * The catalogue overview of `type` narrowed to its packets, carrying `query` along. Falls back to the
 * material catalogue for a type it does not know — a packet whose type failed to load.
 */
export function packetListRoute(type, query) {
  const entry = CATALOG_PACKETS[type] ?? CATALOG_PACKETS.MATERIAL
  return { name: entry.catalog, query: { ...query, kind: CATALOG_KINDS.PACKETS } }
}
